import React, { useState } from 'react';
import { Form, message, Modal } from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginUser, signupUser, verifyOtp, forgotPassword, googleAuth } from '../interfaces/authInterface';
import { initializeAuth, setStatus } from '../features/authSlice';
import { GoogleLogin } from '@react-oauth/google';
import { setCookie } from '../helpers/helper';

/* ── Loader Overlay ── */
function CardLoader({ visible }) {
  if (!visible) return null;
  return (
    <div
      className="absolute inset-0 z-50 flex flex-col items-center justify-center rounded-[22px] gap-4"
      style={{
        background: 'rgba(255,255,255,0.82)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      {/* Spinner */}
      <div className="relative w-12 h-12">
        {/* Outer ring */}
        <div
          className="absolute inset-0 rounded-full border-[3px] border-blue-100"
        />
        {/* Spinning arc */}
        <div
          className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-blue-600 border-r-indigo-500"
          style={{ animation: 'spin 0.75s linear infinite' }}
        />
        {/* Inner dot */}
        <div
          className="absolute inset-[14px] rounded-full bg-gradient-to-br from-blue-500 to-indigo-600"
          style={{ animation: 'pulse 1.5s ease-in-out infinite' }}
        />
      </div>
      <div className="text-[13px] font-semibold text-[#3b4f6b] tracking-wide">
        Please wait…
      </div>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.85); }
        }
      `}</style>
    </div>
  );
}

export default function LoginRegister() {
  const [activeTab, setActiveTab] = useState('login');
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [email, setEmail] = useState('');
  const [pwVisible, setPwVisible] = useState(false);
  const [pw2Visible, setPw2Visible] = useState(false);
  const [remember, setRemember] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);   // ← NEW
  const [resetForm] = Form.useForm();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ── callbacks ── */
  const handleSignup = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const vals = Object.fromEntries(fd.entries());
    if (vals.password !== vals.confirmPassword) { message.error('Passwords do not match!'); return; }
    try {
      setIsLoading(true);
      dispatch(setStatus({ status: 'loading' }));
      await signupUser({ email: vals.email, password: vals.password });
      message.success('Registration successful! Please check your email for the OTP.');
      setEmail(vals.email);
      setShowOtpForm(true);
    } catch (error) {
      message.error(error.response?.data?.error || 'Signup failed.');
      dispatch(setStatus({ status: 'failed', error: error.response?.data?.error || 'Signup failed.' }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerification = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const vals = Object.fromEntries(fd.entries());
    try {
      setIsLoading(true);
      dispatch(setStatus({ status: 'loading' }));
      await verifyOtp({ email, otp: vals.otp }, dispatch);
      message.success('OTP verified successfully!');
      setShowOtpForm(false);
      navigate('/workspace-selector');
      dispatch(setStatus({ status: 'succeeded' }));
    } catch (error) {
      message.error(error.response?.data?.error || 'OTP verification failed.');
      dispatch(setStatus({ status: 'failed', error: error.response?.data?.error || 'OTP verification failed.' }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const vals = Object.fromEntries(fd.entries());
    try {
      const skipOtp = new URLSearchParams(window.location.search).get('skipOtp') === 'true';
      setIsLoading(true);
      dispatch(setStatus({ status: 'loading' }));
      const response = await loginUser(dispatch, { email: vals.email, password: vals.password, skipOtp });
      setEmail(vals.email);
      if (response.canSkip) {
        setCookie('tempAuthToken', response.canSkip);
        dispatch(initializeAuth())
        navigate('/workspace-selector');

        return
       }
      setShowOtpForm(true);
    } catch (error) {
      message.error(error.message || 'Login failed.');
      dispatch(setStatus({ status: 'failed', error: error.message || 'Login failed.' }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = (response) => {
    setIsLoading(true);
    googleAuth({ idToken: response.credential })
      .then(() => {
        message.success('Login successful!');
        dispatch(initializeAuth());
        navigate('/workspace-selector');
        dispatch(setStatus({ status: 'succeeded' }));
      })
      .catch((error) => {
        message.error(error.response?.data?.error || 'Google authentication failed.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const onResetPasswordFinish = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const vals = Object.fromEntries(fd.entries());
    forgotPassword({ email: vals.email, domain: window.location.origin });
    setTimeout(() => {
      message.success(`Password reset link sent to ${vals.email}!`);
      setIsModalVisible(false);
    }, 1500);
  };

  /* ── shared input classes ── */
  const inputCls =
    'w-full px-3.5 py-2.5 bg-[#f7f9ff] border border-[#dde5f4] rounded-[9px] text-[#0c1829] text-sm font-[inherit] outline-none transition-all duration-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 placeholder:text-gray-400';

  const GoogleBtn = () => (
    <div className="flex justify-center">
      <GoogleLogin size="large" onSuccess={handleGoogleAuth} />
    </div>
  );

  const Divider = () => (
    <div className="flex items-center gap-2.5 my-1">
      <span className="flex-1 h-px bg-[#dde5f4]" />
      <span className="text-[#93a3bb] text-xs whitespace-nowrap">Or continue with</span>
      <span className="flex-1 h-px bg-[#dde5f4]" />
    </div>
  );

  /* ── OTP form ── */
  const OtpForm = () => (
    <form onSubmit={handleOtpVerification} className="flex flex-col gap-3.5">
      <div>
        <label className="block text-[13px] font-semibold text-[#0c1829] mb-1.5">Email</label>
        <input className={`${inputCls} opacity-60`} value={email} disabled />
      </div>
      <div>
        <label className="block text-[13px] font-semibold text-[#0c1829] mb-1.5">OTP</label>
        <input name="otp" placeholder="Enter OTP" required className={inputCls} />
      </div>
      <SubmitBtn label="Verify OTP" />
    </form>
  );

  /* ── login form ── */
  const LoginForm = () => (
    <form onSubmit={handleLogin} className="flex flex-col gap-3.5">
      <div>
        <label className="block text-[13px] font-semibold text-[#0c1829] mb-1.5">Email</label>
        <input name="email" id="email" type="email" placeholder="you@company.com" required className={inputCls} />
      </div>
      <div>
        <label className="flex justify-between text-[13px] font-semibold text-[#0c1829] mb-1.5">
          Password
          <a href="#" onClick={e => { e.preventDefault(); setIsModalVisible(true); }} className="text-[12px] text-[#5a6a85] font-normal hover:underline">Forgot?</a>
        </label>
        <div className="relative">
          <input name="password" id="password" type={pwVisible ? 'text' : 'password'} placeholder="Your password" required className={`${inputCls} pr-10`} />
          <span onClick={() => setPwVisible(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-[#93a3bb] text-[15px] select-none">
            {pwVisible ? '🙈' : '👁'}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div
          onClick={() => setRemember(v => !v)}
          className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 cursor-pointer border transition-colors ${remember ? 'bg-blue-600 border-blue-600' : 'bg-[#f7f9ff] border-[#dde5f4]'}`}
        >
          {remember && <span className="text-[9px] text-white font-black">✓</span>}
        </div>
        <span className="text-[13px] text-[#5a6a85]">Remember me</span>
      </div>
      <SubmitBtn label="Log In" />
      <Divider />
      <GoogleBtn />
    </form>
  );

  /* ── signup form ── */
  const SignupForm = () => (
    <form onSubmit={handleSignup} className="flex flex-col gap-3.5">
      <div>
        <label className="block text-[13px] font-semibold text-[#0c1829] mb-1.5">Email</label>
        <input name="email" type="email" placeholder="you@company.com" required className={inputCls} />
      </div>
      <div>
        <label className="block text-[13px] font-semibold text-[#0c1829] mb-1.5">Password</label>
        <div className="relative">
          <input name="password" type={pwVisible ? 'text' : 'password'} placeholder="Password" required className={`${inputCls} pr-10`} />
          <span onClick={() => setPwVisible(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-[#93a3bb] text-[15px] select-none">
            {pwVisible ? '🙈' : '👁'}
          </span>
        </div>
      </div>
      <div>
        <label className="block text-[13px] font-semibold text-[#0c1829] mb-1.5">Re-enter Password</label>
        <div className="relative">
          <input name="confirmPassword" type={pw2Visible ? 'text' : 'password'} placeholder="Re-enter Password" required className={`${inputCls} pr-10`} />
          <span onClick={() => setPw2Visible(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-[#93a3bb] text-[15px] select-none">
            {pw2Visible ? '🙈' : '👁'}
          </span>
        </div>
      </div>
      <SubmitBtn label="Sign Up" />
      <Divider />
      <GoogleBtn />
    </form>
  );

  const SubmitBtn = ({ label }) => (
    <button
      type="submit"
      disabled={isLoading}
      className="w-full py-3 mt-1 bg-gradient-to-br from-blue-600 to-indigo-600 text-white font-bold text-[15px] rounded-[10px] border-none cursor-pointer shadow-[0_4px_18px_rgba(37,99,235,0.32)] hover:opacity-90 transition-opacity font-[inherit] disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {label}
    </button>
  );



return (
  <div className="flex h-screen w-full overflow-hidden font-['Manrope',sans-serif] bg-white text-slate-900">

{/* ══ LEFT — The "Pro" Dark Panel ══ */}
{/* ══ LEFT — The "Pro" Dark Panel ══ */}
<div className="hidden lg:flex lg:w-[60%] xl:w-[65%] relative overflow-hidden bg-[#09090B] flex-col p-8 xl:p-16 border-r border-slate-800">

  {/* Background Decor - Fixed Grid */}
  <div className="absolute inset-0 z-0 pointer-events-none">
    <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(120,119,198,0.15),rgba(255,255,255,0))]" />
    <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
  </div>

  {/* 1. Top Nav */}
  <div className="relative z-10 flex items-center justify-between w-full shrink-0">
    <span className="text-xl xl:text-2xl font-bold text-white tracking-tight">scaleflow.</span>
    <a href="https://scaleflow.in/#demo" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[10px] xl:text-xs font-semibold uppercase tracking-widest text-slate-400 border border-slate-800 rounded-full px-4 py-2 hover:bg-slate-800 transition-all">
      <span>Live Demo</span>
    </a>
  </div>

  {/* 2. Main Content - Flex-1 ensures this area takes up available space */}
  <div className="relative z-10 flex-1 flex flex-col justify-center min-h-0">
    <div className="max-w-3xl">
      {/* Grouped Label and Header to keep them tight together */}
      <div className="space-y-4 xl:space-y-6">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-800/50 border border-slate-700/50 backdrop-blur-md">
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shadow-[0_0_8px_rgba(99,102,241,0.6)]" />
          <span className="text-[9px] xl:text-[11px] font-bold tracking-[0.2em] text-slate-300 uppercase">Enterprise Rental OS</span>
        </div>

        {/* FLUID FONT: Scales based on screen width so it doesn't break on zoom */}
        <h1 className="text-left xl:text-[clamp(3.5rem,5.5vw,6.5rem)] font-bold leading-[1.05] tracking-tighter text-white">
          Run your rentals. <br/>
          <span className="text-slate-600">Without friction.</span><br/>
          <span className="text-slate-600">In your fingertips.</span>
        </h1>

        <p className="text-sm xl:text-lg text-slate-400 leading-relaxed max-w-md font-medium">
          Your entire fleet, billing, and growth engines—at your fingertips.
          <span className="text-slate-200 block mt-1">No spreadsheets, just flow.</span>
        </p>
      </div>
    </div>
  </div>

  {/* 3. Bento Footer - Fixed at bottom with shrink-0 to prevent disappearing */}
  <div className="relative z-10 grid grid-cols-3 gap-4 border-t border-white/5 pt-8 shrink-0">
    {[
      { title: 'Smart Booking', desc: 'Conflict-free scheduling', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
      { title: 'Auto Billing', desc: 'Zero manual invoices', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
      { title: 'Analytics', desc: 'Real-time insights', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' }
    ].map((item) => (
      <div key={item.title} className="flex flex-col gap-2">
        <div className="w-8 h-8 xl:w-10 xl:h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
          <svg className="w-4 h-4 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
          </svg>
        </div>
        <div className="space-y-0.5">
          <h3 className="text-slate-200 font-bold text-[11px] xl:text-xs tracking-tight">{item.title}</h3>
          <p className="text-slate-500 text-[9px] xl:text-[10px] leading-tight">{item.desc}</p>
        </div>
      </div>
    ))}
  </div>
</div>

    {/* ══ RIGHT — The Clean Interface (Stable & Centered) ══ */}
    <div className="flex-1 flex flex-col justify-center relative z-20 bg-white">
      {/* Container forced to take height, keeping form perfectly centered without jumping */}
      <div className="w-full max-w-[440px] mx-auto px-8 lg:px-12 py-10 flex flex-col justify-center h-full">

        {/* Header */}
        <div className="mb-8 text-center sm:text-left">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">
            {activeTab === 'login' ? 'Welcome back' : 'Create an account'}
          </h2>
          <p className="text-slate-500 text-sm font-medium">
            Enter your details to access your workspace.
          </p>
        </div>

        <CardLoader visible={isLoading} />

        {showOtpForm ? (
          <OtpForm />
        ) : (
          <div className="w-full">
            {/* High-end Segmented Control */}
            <div className="flex p-1 bg-slate-100/80 rounded-xl mb-8 border border-slate-200/50">
              {['login', 'signup'].map((t) => (
                <button
                  key={t}
                  onClick={() => setActiveTab(t)}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold capitalize transition-all duration-300 ${
                    activeTab === t
                      ? 'bg-white text-slate-900 shadow-sm border border-slate-200/50'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {t === 'login' ? 'Log in' : 'Sign up'}
                </button>
              ))}
            </div>

            {/* Fixed-height form container ensures toggling tabs doesn't shift the page */}
            <div className="min-h-[360px]">
              {activeTab === 'login' ? <LoginForm /> : <SignupForm />}
            </div>
          </div>
        )}

        <div className="mt-8 text-center sm:text-left">
          <p className="text-xs text-slate-500 font-medium">
            By continuing, you agree to our{' '}
            <a href="#" className="text-slate-900 hover:underline decoration-slate-300 underline-offset-4">Terms</a>
            {' '}and{' '}
            <a href="#" className="text-slate-900 hover:underline decoration-slate-300 underline-offset-4">Privacy</a>.
          </p>
        </div>
      </div>
    </div>
  </div>
);
}


